<header class="url-detail-hero" *ngIf="!isEmbedMode()">
  <h2 class="url-detail-title">URL Detail</h2>
  <a
    class="url-detail-page-link"
    [href]="url"
    target="_blank"
    rel="noopener noreferrer"
    *ngIf="url"
  >
    <span class="url-detail-page-link-text">{{ url }}</span>
    <span class="url-detail-page-link-icon" aria-hidden="true">↗</span>
  </a>
  <div class="url-detail-dev-actions" *ngIf="isDevView() && url">
    <button
      type="button"
      class="btn-secondary btn-sm"
      (click)="refetchSinglePageAnalysis()"
      [disabled]="pageRefetchRunning"
      title="Dev-only: force a fresh analysis task for this URL only."
    >{{ pageRefetchRunning ? 'Re-fetching page…' : 'Re-fetch This Page' }}</button>
    <button
      type="button"
      class="btn-secondary btn-sm"
      (click)="devRebuildSupportingPagesByTopic()"
      [disabled]="supportingPagesV1RebuildRunning"
      title="Dev-only: rebuild Supporting pages by topic snapshot for this URL (replaces stored data)."
    >{{ supportingPagesV1RebuildRunning ? 'Building supporting pages…' : 'Build Supporting Pages' }}</button>
    <button
      type="button"
      class="btn-secondary btn-sm"
      (click)="devRebuildSupportingPagesOpportunities()"
      [disabled]="supportingPagesOppRebuildRunning"
      title="Dev-only: rebuild Supporting page opportunities snapshot for this URL (replaces stored data)."
    >{{ supportingPagesOppRebuildRunning ? 'Building opportunities…' : 'Build Opportunities' }}</button>
    <span class="muted" *ngIf="pageRefetchStatus">{{ pageRefetchStatus }}</span>
    <span class="muted" *ngIf="supportingPagesRebuildStatus">{{ supportingPagesRebuildStatus }}</span>
  </div>
</header>

<section class="url-focus" aria-label="Focus">
  <p class="url-focus-status muted" *ngIf="analysisStatus === 'loading'">Loading health and SERP context…</p>
  <p class="url-focus-status muted" *ngIf="analysisStatus === 'error' || (analysisStatus === 'success' && !analysisData)">
    Run a crawl and analysis for this URL to see health and SERP context.
  </p>

  <ng-container *ngIf="analysisStatus === 'success' && analysisData">
    <section
      class="url-focus-row url-focus-row--next-action"
      *ngIf="!isPlanV2Embed() && isActionPlanV2Enabled()"
      aria-labelledby="url-next-action-heading"
    >
      <h3 id="url-next-action-heading" class="url-focus-row-heading">Next action</h3>
      <p class="muted" *ngIf="planV2NextActionLoading">Loading synthesis…</p>
      <p class="url-focus-error muted" *ngIf="planV2NextActionError">{{ planV2NextActionError }}</p>
      <article class="url-focus-card url-focus-card--next-action" *ngIf="planV2NextAction && !planV2NextActionLoading">
        <p class="url-next-action-primary">
          <strong>{{ planV2NextAction.recommendedAction.label }}</strong>
          <span class="score-badge url-next-action-score">{{ planV2NextAction.priorityScore | number:'1.0-0' }}</span>
        </p>
        <ul class="url-next-action-explain muted">
          <li *ngFor="let line of planV2NextAction.explain.slice(0, 5)">{{ line }}</li>
        </ul>
        <button type="button" class="btn-secondary btn-sm" (click)="openPlanV2InInventory()">View in Plan v2</button>
      </article>
      <p class="muted" *ngIf="!planV2NextActionLoading && !planV2NextActionError && !planV2NextAction">
        No open recommended action in Plan v2 for this URL.
      </p>
    </section>

    <section class="url-focus-row url-focus-row--embed-mini" *ngIf="showEmbedMiniOverviewSection()" aria-labelledby="url-embed-overview-heading">
      <h3 id="url-embed-overview-heading" class="url-focus-row-heading">Overview</h3>
      <div class="url-focus-row-cards url-focus-row-cards--overview">
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Accountable for</h4>
          <p class="url-focus-stat-value" [ngClass]="pageDemandClaimClass()" [attr.title]="pageDemandClaimTitle()">{{ pageDemandClaimLabel() }}</p>
        </article>
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Keywords</h4>
          <p class="url-focus-stat-value" [attr.title]="rankedKeywordsCountTitle()">{{ rankedKeywordsCountLabel() }}</p>
        </article>
      </div>
    </section>

    <section class="url-focus-row" *ngIf="!isPlanV2Embed()" aria-labelledby="url-overview-heading">
      <h3 id="url-overview-heading" class="url-focus-row-heading">Overview</h3>
      <div class="url-focus-row-cards url-focus-row-cards--overview">
        <article class="url-focus-card url-focus-card-status">
          <h4 class="url-focus-card-title">Health</h4>
          <p class="url-focus-status-value">
            <span class="health-pill url-focus-status-pill" [ngClass]="effectiveHealthClass()">{{ effectiveHealthLabel() }}</span>
          </p>
        </article>
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Status code</h4>
          <p class="url-focus-stat-value">{{ analysisData.statusCode }}</p>
        </article>
        <article class="url-focus-card url-focus-card-onpage">
          <h4 class="url-focus-card-title">Technical OnPage Score</h4>
          <p class="url-focus-onpage-score" [ngClass]="getScoreClass(analysisData.onpageScore)">
            {{ technicalOnPageScoreLabel() }}
          </p>
          <p class="url-focus-onpage-issues muted" *ngIf="technicalOnPageIssuesLabel() as issues">{{ issues }}</p>
        </article>
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Accountable for</h4>
          <p class="url-focus-stat-value" [ngClass]="pageDemandClaimClass()" [attr.title]="pageDemandClaimTitle()">{{ pageDemandClaimLabel() }}</p>
        </article>
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Keywords</h4>
          <p class="url-focus-stat-value" [attr.title]="rankedKeywordsCountTitle()">{{ rankedKeywordsCountLabel() }}</p>
        </article>
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Est. traffic</h4>
          <p class="url-focus-stat-value" [attr.title]="estimatedTrafficTitle()">{{ estimatedTrafficLabel() }}</p>
        </article>
        <article class="url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Gap · visibility</h4>
          <p class="url-focus-stat-value" [attr.title]="pageRealizationStatTitle()">{{ pageRealizationStatLabel() }}</p>
        </article>
        <article class="url-focus-card url-focus-card-log" *ngIf="isDevView()">
          <h4 class="url-focus-card-title">Log</h4>
          <p class="url-focus-card-lead">{{ improvementsSummary() }}</p>
          <ul class="url-focus-list" *ngIf="improvementsWhyBullets().length">
            <li *ngFor="let bullet of improvementsWhyBullets()">{{ bullet }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section class="url-focus-row" *ngIf="showEmbedContentSection()" aria-labelledby="url-content-heading">
      <h3 id="url-content-heading" class="url-focus-row-heading">Content &amp; Topics</h3>
      <div class="url-focus-row-cards url-focus-row-cards--content">
        <article class="url-focus-card url-focus-card-stat url-focus-card-wordcount" *ngIf="!isPlanV2Embed()">
          <h4 class="url-focus-card-title">Word count</h4>
          <p class="url-focus-stat-value">{{ analysisData.wordCount.toLocaleString() }}</p>
        </article>

        <article
          *ngIf="!isPlanV2Embed()"
          class="url-focus-card url-focus-card-topics"
          [class.url-focus-card-topics--user]="!isDevView()"
        >
          <h4 class="url-focus-card-title">Top topics</h4>
          <p class="url-content-topics-empty muted" *ngIf="!hasTopicSignals()">
            No topic signals yet. They appear after keyword density and topic mapping have run for this page.
          </p>
          <div class="url-top-topics-chips" *ngIf="hasTopicSignals() && !isDevView()">
            <span
              class="url-top-topic-chip"
              *ngFor="let signal of getTopPageTopicSignals(); trackBy: trackByTopicSignal"
            >
              <span class="url-top-topic-chip-label">{{ signal.label }}</span>
              <span
                class="url-top-topic-chip-pct"
                [ngClass]="getScoreClass(pageTopicRelevanceScore0to100(signal))"
              >{{ formatPageTopicRelevancePct(signal) }}</span>
            </span>
          </div>
          <ng-container *ngIf="hasTopicSignals() && isDevView()">
            <div class="page-topics-table-wrap" *ngIf="!pageTopicsExpanded">
              <table class="page-topics-table">
                <thead>
                  <tr>
                    <th scope="col">Topic</th>
                    <th scope="col">Competitors</th>
                    <th scope="col" class="num">Relevance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let signal of getTopPageTopicSignals(); trackBy: trackByTopicSignal">
                    <td class="topic-label">{{ signal.label }}</td>
                    <td class="topic-competitors">
                      <ng-container *ngTemplateOutlet="topicCompetitorsCell; context: { signal: signal }"></ng-container>
                    </td>
                    <td class="num">
                      <span
                        class="topic-relevance-badge"
                        [ngClass]="getScoreClass(pageTopicRelevanceScore0to100(signal))"
                      >
                        {{ formatPageTopicRelevancePct(signal) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="page-topics-table-wrap" *ngIf="pageTopicsExpanded">
              <table class="page-topics-table">
                <thead>
                  <tr>
                    <th scope="col">Topic</th>
                    <th scope="col">Slug</th>
                    <th scope="col">Competitors</th>
                    <th scope="col" class="num">Weight</th>
                    <th scope="col" class="num">Salience</th>
                    <th scope="col" class="num">Rel. density</th>
                    <th scope="col" class="num">Relevance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let signal of getPageTopicSignals(); trackBy: trackByTopicSignal">
                    <td class="topic-label">{{ signal.label }}</td>
                    <td class="topic-slug"><code>{{ signal.topicSlug }}</code></td>
                    <td class="topic-competitors">
                      <ng-container *ngTemplateOutlet="topicCompetitorsCell; context: { signal: signal }"></ng-container>
                    </td>
                    <td class="num">{{ formatTopicMetric(signal.weight) }}</td>
                    <td class="num">{{ formatTopicMetric(signal.salience) }}</td>
                    <td class="num">{{ formatTopicMetric(signal.relativeDensity) }}</td>
                    <td class="num">
                      <span
                        class="topic-relevance-badge"
                        [ngClass]="getScoreClass(pageTopicRelevanceScore0to100(signal))"
                      >
                        {{ formatPageTopicRelevancePct(signal) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <button
              type="button"
              class="url-content-topics-expand"
              *ngIf="hasMorePageTopicsThanTop() || pageTopicsExpanded"
              (click)="togglePageTopicsExpanded()"
              [attr.aria-expanded]="pageTopicsExpanded"
            >{{ pageTopicsExpandControlLabel() }}</button>
          </ng-container>
        </article>

        <article
          *ngIf="showEmbedSupportingPagesCard() && buildTopicOwnershipIssuePayload() as topicPayload"
          class="url-focus-card url-focus-card-topic-ownership url-focus-card-topic-ownership--unified"
          [class.url-focus-card--composing]="topicOwnershipComposing"
          [ngClass]="planV2CardExitClass(topicPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Supporting pages</h4>
            <span
              class="url-focus-diagnosis-chip"
              *ngIf="showTopicOwnershipGapChip()"
              [ngClass]="topicOwnershipGapChipClass()"
            >{{ topicOwnershipGapChipLabel() }}</span>
          </div>

          <ng-template
            #headingPathTree
            let-segments
            let-composeSelect="composeSelect"
            let-v2Row="v2Row"
            let-sc="sc"
          >
            <div class="url-topic-ownership-v2-heading-tree">
              <div
                class="url-topic-ownership-v2-heading-tree-row"
                [class.url-topic-ownership-v2-heading-tree-row--compose-pick]="composeSelect"
                [class.url-topic-ownership-v2-heading-tree-row--compose-selected]="
                  composeSelect && isTopicOwnershipComposeHeadingSelected(v2Row, sc, depth)
                "
                [attr.role]="composeSelect ? 'button' : null"
                [attr.tabindex]="composeSelect ? 0 : null"
                [attr.aria-pressed]="
                  composeSelect ? isTopicOwnershipComposeHeadingSelected(v2Row, sc, depth) : null
                "
                (click)="composeSelect && toggleTopicOwnershipComposeHeading($event, v2Row, sc, depth)"
                (keydown.enter)="composeSelect && toggleTopicOwnershipComposeHeading($event, v2Row, sc, depth)"
                (keydown.space)="composeSelect && toggleTopicOwnershipComposeHeading($event, v2Row, sc, depth)"
                *ngFor="let seg of segments; let depth = index"
                [style.padding-left.rem]="(seg.treeDepth ?? depth) * 1.15"
              >
                <span
                  class="url-topic-ownership-v2-heading-level-chip"
                  *ngIf="topicOwnershipV2HeadingLevelChip(seg.level) as chip"
                  >{{ chip }}</span
                >
                <span
                  class="url-topic-ownership-v2-heading-path-segment"
                  [ngClass]="topicOwnershipV2SectionDisplayHeadingRole(seg)"
                  >{{ seg.heading }}</span
                >
              </div>
            </div>
          </ng-template>

          <ng-template #sectionComparePanel let-v2Row="row" let-composeSelect="composeSelect">
            <div class="url-topic-ownership-v2-detail url-topic-ownership-v2-heatmap url-topic-ownership-col-compare">
              <p
                class="url-topic-ownership-v2-listicle-note muted"
                *ngIf="topicOwnershipV2HasListInventory(v2Row) && (composeSelect || !isDevView())"
              >
                <ng-container *ngIf="composeSelect; else listicleNoteBrowse">
                  Roundup detected — pick missing <strong>tools</strong> above; section compare here is for
                  <strong>feature depth</strong> on tools you both cover.
                </ng-container>
                <ng-template #listicleNoteBrowse>
                  Roundup detected — open <strong>List inventory</strong> on the competitor row for
                  missing tools; section compare here is for feature depth.
                </ng-template>
              </p>
              <div class="url-topic-ownership-v2-compare-toolbar" *ngIf="isDevView() && !composeSelect">
                <p class="url-topic-ownership-v2-detail-lead">
                  <strong>What they cover that your page doesn’t</strong>
                  <span class="muted"> — their sections vs your whole focal page</span>
                </p>
                <div class="url-topic-ownership-v2-compare-filter" role="group" aria-label="Compare detail filter">
                  <button
                    type="button"
                    class="url-topic-ownership-v2-compare-filter-btn"
                    [class.url-topic-ownership-v2-compare-filter-btn--active]="
                      topicOwnershipV2CompareFilterMode() === 'not_on_page'
                    "
                    (click)="setTopicOwnershipV2CompareFilter('not_on_page')"
                  >
                    Not on your page
                  </button>
                  <button
                    type="button"
                    class="url-topic-ownership-v2-compare-filter-btn"
                    [class.url-topic-ownership-v2-compare-filter-btn--active]="
                      topicOwnershipV2CompareFilterMode() === 'gaps'
                    "
                    (click)="setTopicOwnershipV2CompareFilter('gaps')"
                  >
                    Gaps only
                  </button>
                  <button
                    type="button"
                    class="url-topic-ownership-v2-compare-filter-btn"
                    [class.url-topic-ownership-v2-compare-filter-btn--active]="
                      topicOwnershipV2CompareFilterMode() === 'all'
                    "
                    (click)="setTopicOwnershipV2CompareFilter('all')"
                  >
                    All sections
                  </button>
                </div>
              </div>

              <p
                class="muted url-topic-ownership-v2-compare-filter-empty"
                *ngIf="
                  isDevView() &&
                  !composeSelect &&
                  topicOwnershipV2CompareFilterMode() !== 'all' &&
                  !topicOwnershipV2FilteredSectionCoverageRows(v2Row).length
                "
              >
                {{ topicOwnershipV2CompareFilterEmptyMessage() }}
              </p>

              <div
                class="url-topic-ownership-v2-heatmap-section"
                [class.url-topic-ownership-v2-heatmap-section--missing-only]="!sc.sectionCovered"
                *ngFor="
                  let sc of composeSelect
                    ? topicOwnershipComposeSectionRows(v2Row)
                    : topicOwnershipV2FilteredSectionCoverageRows(v2Row)
                "
              >
                <div class="url-topic-ownership-v2-heatmap-section-top">
                  <ng-container *ngIf="isDevView(); else userSectionTopChips">
                    <span
                      class="url-topic-ownership-v2-page-chip"
                      *ngIf="topicOwnershipV2SectionPositionChip(sc)"
                      [title]="'Section ' + topicOwnershipV2SectionPositionChip(sc) + ' on competitor page'"
                      >{{ topicOwnershipV2SectionPositionChip(sc) }}</span
                    >
                    <span
                      class="url-topic-ownership-v2-section-status"
                      [ngClass]="topicOwnershipV2SectionStatusClass(sc)"
                      [title]="topicOwnershipV2SectionStatusTitle(sc)"
                      >{{ topicOwnershipV2SectionStatusLabel(sc) }}</span
                    >
                  </ng-container>
                  <ng-template #userSectionTopChips>
                    <span
                      class="url-topic-ownership-v2-section-status"
                      [ngClass]="topicOwnershipV2SectionStatusClass(sc)"
                      [title]="topicOwnershipV2SectionStatusTitle(sc)"
                      >{{ topicOwnershipV2SectionStatusLabel(sc) }}</span
                    >
                    <span
                      class="url-topic-ownership-v2-page-chip"
                      *ngIf="topicOwnershipV2SectionPositionChip(sc)"
                      [title]="'Section ' + topicOwnershipV2SectionPositionChip(sc) + ' on competitor page'"
                      >{{ topicOwnershipV2SectionPositionChip(sc) }}</span
                    >
                  </ng-template>
                </div>

                <div
                  class="url-topic-ownership-v2-section-heading-line"
                  *ngIf="!topicOwnershipV2SectionCompareExpanded(v2Row.competitorUrl, sc, composeSelect)"
                >
                  <span
                    class="url-topic-ownership-v2-page-chip"
                    *ngIf="isDevView()"
                    [title]="v2Row.competitorUrl"
                    >{{ topicOwnershipV2RowDomain(v2Row.competitorUrl) }}</span
                  >
                  <ng-container
                    *ngTemplateOutlet="
                      headingPathTree;
                      context: topicOwnershipHeadingTreeContext(v2Row, sc, composeSelect)
                    "
                  ></ng-container>
                </div>

                <p
                  class="url-topic-ownership-v2-missing-note url-topic-ownership-v2-non-topical-note"
                  *ngIf="isDevView() && sc.isNonTopical"
                >
                  {{ topicOwnershipV2NonTopicalStatusTitle(sc) }}
                </p>

                <p
                  class="url-topic-ownership-v2-missing-note"
                  *ngIf="isDevView() && !sc.sectionCovered && !sc.isNonTopical"
                >
                  Likely not covered on your page — no focal section reached the similarity threshold.
                </p>

                <div
                  class="url-topic-ownership-v2-missing-block"
                  *ngIf="!topicOwnershipV2SectionCompareExpanded(v2Row.competitorUrl, sc, composeSelect) && sc.sectionCovered && sc.absentTerms?.length"
                >
                  <h6 class="url-topic-ownership-v2-talking-heading">Phrases they cover, you don’t</h6>
                  <ul class="url-topic-ownership-v2-missing-only-gaps">
                    <li class="url-topic-ownership-v2-talking-point--missing" *ngFor="let term of sc.absentTerms">
                      {{ term }}
                    </li>
                  </ul>
                </div>

                <p
                  class="muted url-topic-ownership-v2-section-allcovered"
                  *ngIf="!composeSelect && sc.sectionCovered && !sc.absentTerms?.length && !sc.isNonTopical"
                >
                  You cover this section’s topic and its key phrases.
                </p>

                <button
                  type="button"
                  class="url-content-topics-expand url-topic-ownership-v2-fulltext-toggle"
                  *ngIf="!composeSelect && topicOwnershipV2SectionCompareAvailable(sc)"
                  (click)="toggleTopicOwnershipV2SectionFullText(v2Row.competitorUrl, sc); $event.stopPropagation()"
                >
                  {{
                    isTopicOwnershipV2SectionFullTextExpanded(v2Row.competitorUrl, sc)
                      ? 'Hide side-by-side compare'
                      : 'Show side-by-side compare'
                  }}
                </button>

                <div
                  class="url-topic-ownership-v2-compare-columns"
                  *ngIf="
                    topicOwnershipV2SectionCompareAvailable(sc) &&
                    topicOwnershipV2SectionCompareExpanded(v2Row.competitorUrl, sc, composeSelect)
                  "
                >
                  <div class="url-topic-ownership-v2-compare-col-stack">
                    <span
                      class="url-topic-ownership-v2-page-chip url-topic-ownership-v2-compare-above-chip"
                      [title]="v2Row.competitorUrl"
                      >{{ topicOwnershipV2RowDomain(v2Row.competitorUrl) }}</span
                    >
                    <div class="url-topic-ownership-v2-compare-col">
                      <div class="url-topic-ownership-v2-compare-col-title">
                        <ng-container
                          *ngTemplateOutlet="
                            headingPathTree;
                            context: topicOwnershipHeadingTreeContext(v2Row, sc, composeSelect)
                          "
                        ></ng-container>
                      </div>
                      <div
                        class="url-topic-ownership-v2-compare-body"
                        *ngIf="topicOwnershipV2SectionBodyText(sc); else compSectionEmpty"
                      >
                        <span
                          *ngFor="let seg of topicOwnershipV2SectionHighlightSegments(sc)"
                          [class.url-topic-ownership-v2-hl--missing]="seg.highlighted"
                          >{{ seg.text }}</span
                        >
                      </div>
                      <ng-template #compSectionEmpty>
                        <p class="muted url-topic-ownership-v2-compare-empty">No section body text.</p>
                      </ng-template>
                    </div>
                  </div>

                  <div class="url-topic-ownership-v2-compare-col-stack">
                    <span
                      class="url-topic-ownership-v2-page-chip url-topic-ownership-v2-compare-above-chip"
                      *ngIf="topicOwnershipV2CompareClosestLabel(sc) as closestLabel"
                      [title]="topicOwnershipV2ClosestMatchTitle(sc)"
                      >{{ closestLabel }}</span
                    >
                    <div class="url-topic-ownership-v2-compare-col">
                      <div class="url-topic-ownership-v2-compare-col-title">
                        <span class="url-topic-ownership-v2-page-chip" *ngIf="isDevView()" [title]="url || ''">{{
                          topicOwnershipV2FocalDomain()
                        }}</span>
                        <ng-container
                          *ngTemplateOutlet="
                            headingPathTree;
                            context: { $implicit: topicOwnershipV2CompareRightColumnSegments(sc) }
                          "
                        ></ng-container>
                      </div>
                      <div
                        class="url-topic-ownership-v2-compare-body"
                        *ngIf="topicOwnershipV2ClosestMyBodyText(sc); else mySectionEmpty"
                      >
                        {{ topicOwnershipV2ClosestMyBodyText(sc) }}
                      </div>
                      <ng-template #mySectionEmpty>
                        <p class="muted url-topic-ownership-v2-compare-empty">
                          {{
                            topicOwnershipV2HasClosestMatch(sc)
                              ? 'No body text in the closest focal section.'
                              : 'Nothing on your focal URL matched this section.'
                          }}
                        </p>
                      </ng-template>
                    </div>
                  </div>
                </div>
              </div>

              <span class="muted" *ngIf="v2Row.error"> — {{ v2Row.error }}</span>
            </div>
          </ng-template>

          <ng-container *ngIf="topicOwnershipComposing && topicOwnershipComposeState as composeState">
            <p class="plan-v2-compose-title url-topic-ownership-compose-title">What should we add?</p>

            <div class="url-topic-ownership-compose-block" *ngIf="topicOwnershipComposeHasPageGaps()">
              <h5 class="url-topic-ownership-compose-heading">Pages</h5>
              <p class="url-topic-ownership-compose-lead muted">
                Whole supporting URLs missing on your site — check any you want to add.
              </p>
              <div class="url-topic-ownership-compose-pages">
                <label class="url-topic-ownership-compose-page" *ngFor="let page of composeState.pageGaps">
                  <input type="checkbox" [(ngModel)]="page.selected" [name]="'compose-page-' + page.id" />
                  <span class="url-topic-ownership-compose-page-body">
                    <span class="url-topic-ownership-compose-page-path">
                      <strong>{{ page.competitorDomain }}</strong>{{ page.path }}
                    </span>
                    <span class="url-topic-ownership-compose-page-meta muted">{{ page.topicLabel }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="url-topic-ownership-compose-block" *ngIf="topicOwnershipComposeHasToolGaps()">
              <h5 class="url-topic-ownership-compose-heading">Tools to cover</h5>
              <p class="url-topic-ownership-compose-lead muted">
                On their roundup but not on your page — check any you want to add.
              </p>
              <div
                class="url-topic-ownership-compose-competitor-pick"
                *ngIf="composeState.listCompetitors.length"
              >
                <ng-container *ngIf="composeState.listCompetitors.length > 1; else composeSingleListCompetitor">
                  <label class="url-topic-ownership-compare-label" for="topic-ownership-compose-list-competitor"
                    >Competitor roundup</label
                  >
                  <select
                    id="topic-ownership-compose-list-competitor"
                    class="url-topic-ownership-compare-select"
                    [ngModel]="composeState.activeListCompetitorUrl"
                    (ngModelChange)="onTopicOwnershipComposeListCompetitorChange($event)"
                  >
                    <option *ngFor="let opt of composeState.listCompetitors" [value]="opt.url">
                      {{ opt.label }} ({{ opt.missingToolCount }} missing)
                    </option>
                  </select>
                </ng-container>
                <ng-template #composeSingleListCompetitor>
                  <p class="url-topic-ownership-compose-competitor-label muted">
                    From <strong>{{ composeState.listCompetitors[0].label }}</strong>
                    · {{ composeState.listCompetitors[0].missingToolCount }} missing tools
                  </p>
                </ng-template>
              </div>
              <div class="url-topic-ownership-compose-pages url-topic-ownership-compose-tools">
                <label class="url-topic-ownership-compose-page" *ngFor="let tool of topicOwnershipComposeToolsForActiveCompetitor()">
                  <input type="checkbox" [(ngModel)]="tool.selected" [name]="'compose-tool-' + tool.id" />
                  <span class="url-topic-ownership-compose-page-body">
                    <span class="url-topic-ownership-compose-page-path">{{ tool.label }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="url-topic-ownership-compose-block" *ngIf="topicOwnershipComposeHasSectionGaps()">
              <h5 class="url-topic-ownership-compose-heading">Sections</h5>
              <div
                class="url-topic-ownership-compose-competitor-pick"
                *ngIf="composeState.sectionCompetitors.length"
              >
                <ng-container *ngIf="composeState.sectionCompetitors.length > 1; else composeSingleCompetitor">
                  <label class="url-topic-ownership-compare-label" for="topic-ownership-compose-competitor"
                    >Competitor page</label
                  >
                  <select
                    id="topic-ownership-compose-competitor"
                    class="url-topic-ownership-compare-select"
                    [ngModel]="composeState.activeCompetitorUrl"
                    (ngModelChange)="onTopicOwnershipComposeCompetitorChange($event)"
                  >
                    <option *ngFor="let opt of composeState.sectionCompetitors" [value]="opt.url">
                      {{ opt.label }} ({{ opt.missingCount }} missing)
                    </option>
                  </select>
                </ng-container>
                <ng-template #composeSingleCompetitor>
                  <p class="url-topic-ownership-compose-competitor-label muted">
                    From <strong>{{ composeState.sectionCompetitors[0].label }}</strong>
                    · {{ composeState.sectionCompetitors[0].missingCount }} missing sections
                  </p>
                </ng-template>
              </div>
              <div
                class="url-topic-ownership-section-compare-fullrow url-topic-ownership-section-compare-fullrow--compose"
                *ngIf="topicOwnershipComposeActiveV2Row() as composeV2Row"
              >
                <ng-container
                  *ngTemplateOutlet="sectionComparePanel; context: { row: composeV2Row, composeSelect: true }"
                ></ng-container>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="!topicOwnershipComposing">
          <p
            class="url-topic-ownership-hint-user muted"
            *ngIf="supportingPagesColumnsReady()"
          >
            Top page per domain for this URL’s focal topics<ng-container *ngIf="topicOwnershipAnchorHint()"
              > ({{ topicOwnershipAnchorHint() }})</ng-container
            >.
            <span class="url-topic-ownership-coverage-legend url-topic-ownership-link--covered">Covered</span> ·
            <span class="url-topic-ownership-coverage-legend url-topic-ownership-link--gap">Gap</span> on competitor
            supporting URLs.
          </p>

          <div
            class="url-topic-ownership-skeleton"
            *ngIf="supportingPagesUnifiedLoading()"
            aria-busy="true"
            aria-label="Loading supporting pages"
          >
            <div class="url-topic-ownership-skeleton-columns">
              <div class="url-topic-ownership-skeleton-col" *ngFor="let _ of topicOwnershipSkeletonCols">
                <div class="url-topic-ownership-skeleton-title"></div>
                <div class="url-topic-ownership-skeleton-line" *ngFor="let __ of topicOwnershipSkeletonLines"></div>
              </div>
            </div>
          </div>

          <p
            class="url-topic-ownership-status muted"
            *ngIf="topicOwnershipStatus === 'ready' && topicOwnershipV2Status !== 'loading' && !supportingPagesColumnsReady() && !supportingPagesUnifiedLoading()"
          >
            <ng-container *ngIf="topicOwnershipCacheMiss; else supportingPagesEmptyUnified">
              No precomputed snapshot yet — run <strong>Supporting pages by topic</strong> and
              <strong>Supporting page opportunities</strong> from Crawl Management.
            </ng-container>
            <ng-template #supportingPagesEmptyUnified>
              No supporting pages found for the strongest topics on this URL.
            </ng-template>
          </p>

          <p
            class="url-topic-ownership-status url-topic-ownership-v2-error"
            *ngIf="topicOwnershipV2Status === 'error'"
          >
            {{ topicOwnershipV2Error }}
            <button type="button" class="url-content-topics-expand" (click)="retryTopicOwnershipV2Test()">Retry</button>
          </p>

          <div
            class="url-topic-ownership-columns"
            *ngIf="supportingPagesColumnsReady()"
          >
            <div
              class="url-topic-ownership-col"
              *ngFor="let col of getSupportingPagesColumns()"
              [class.url-topic-ownership-col--you]="col.isYourSite"
            >
              <h5 class="url-topic-ownership-col-title">{{ col.domain }}</h5>

              <div class="url-topic-ownership-col-hero" *ngIf="col.heroUrl">
                <a
                  class="url-topic-ownership-link url-topic-ownership-link--hero"
                  [href]="col.heroUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  [class.url-topic-ownership-link--current]="isCurrentPageSupportingUrl(col.heroUrl)"
                  [title]="topicOwnershipHeroLinkTitle(col)"
                >
                  <span class="url-topic-ownership-link-path">{{ topicOwnershipPagePath(col.heroUrl) }}</span
                  ><span
                    class="url-topic-ownership-link-traffic"
                    *ngIf="topicOwnershipPageTraffic(col.heroRow?.estimatedOrganicTraffic) as heroTraffic"
                  >
                    ({{ heroTraffic }})</span
                  >
                </a>

                <div class="url-topic-ownership-col-hero-topics" *ngIf="isDevView() && col.heroRow?.topics?.length">
                  <span
                    class="url-topic-chip"
                    *ngFor="let chip of col.heroRow!.topics"
                    [class.url-topic-chip--main]="chip.role === 'main'"
                    [class.url-topic-chip--supporting]="chip.role === 'supporting'"
                    >{{ chip.topicLabel }}</span
                  >
                </div>

                <div class="url-topic-ownership-col-hero-chips" *ngIf="!col.isYourSite && col.v2Row as v2Row">
                  <span
                    class="url-topic-ownership-v2-page-chip url-topic-ownership-v2-serp-chip"
                    *ngIf="topicOwnershipV2ShowSerpRankChip(v2Row.competitorUrl)"
                    [ngClass]="topicOwnershipV2SerpRankChipClass(v2Row.competitorUrl)"
                    [title]="topicOwnershipV2SerpRankChipTitle(v2Row.competitorUrl)"
                    >{{ topicOwnershipV2SerpRankChipLabel(v2Row.competitorUrl) }}</span
                  >
                  <button
                    type="button"
                    class="url-topic-ownership-v2-page-chip url-topic-ownership-v2-list-inventory-chip"
                    *ngIf="topicOwnershipV2HasListInventory(v2Row)"
                    [class.url-topic-ownership-v2-list-inventory-chip--open]="isTopicOwnershipV2ListInventoryOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2ListInventoryOpen(col)"
                    [title]="topicOwnershipV2ListInventoryChipTitle(v2Row)"
                    (click)="toggleTopicOwnershipV2ListInventory(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2ListInventoryOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2ListInventoryChipLabel(v2Row)
                    }}</span>
                  </button>
                  <button
                    type="button"
                    class="url-topic-ownership-v2-page-chip url-topic-ownership-v2-missing-chip"
                    *ngIf="v2Row.missingSectionCount > 0 && topicOwnershipV2HasSectionCoverage(v2Row)"
                    [class.url-topic-ownership-v2-missing-chip--open]="isTopicOwnershipV2CompareOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2CompareOpen(col)"
                    (click)="toggleTopicOwnershipV2Detail(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2CompareOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2MissingSectionsChipLabel(v2Row)
                    }}</span>
                  </button>
                  <button
                    type="button"
                    class="url-topic-ownership-v2-page-chip url-topic-ownership-v2-covered-chip"
                    [class.url-topic-ownership-v2-covered-chip--open]="isTopicOwnershipV2CoveredElsewhereOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2CoveredElsewhereOpen(col)"
                    (click)="toggleTopicOwnershipV2CoveredElsewhere(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2CoveredElsewhereOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2CoveredElsewhereChipLabel(v2Row)
                    }}</span>
                  </button>
                </div>
              </div>

              <div
                class="url-topic-ownership-list-inventory-card"
                *ngIf="!col.isYourSite && col.v2Row && isTopicOwnershipV2ListInventoryOpen(col)"
              >
                <p class="url-topic-ownership-list-inventory-lead">
                  On their roundup but not mentioned on your page
                </p>
                <ul class="url-topic-ownership-list-inventory-items">
                  <li *ngFor="let item of col.v2Row.listInventoryDiff?.competitorOnly">
                    {{ item.label }}
                  </li>
                </ul>
                <p
                  class="muted url-topic-ownership-list-inventory-meta"
                  *ngIf="col.v2Row.listInventoryDiff?.onBoth?.length"
                >
                  {{ col.v2Row.listInventoryDiff!.onBoth!.length }} shared ·
                  {{ col.v2Row.listInventoryDiff!.competitorItemCount }} on theirs ·
                  {{ col.v2Row.listInventoryDiff!.focalItemCount }} on yours
                </p>
                <p class="muted url-topic-ownership-list-inventory-note" *ngIf="!isDevView()">
                  Roundup vs roundup — use this for missing <strong>tools</strong>. Section compare
                  below is for <strong>feature depth</strong> on tools you both cover.
                </p>
              </div>

              <div
                class="url-topic-ownership-covered-card"
                *ngIf="!col.isYourSite && col.v2Row && isTopicOwnershipV2CoveredElsewhereOpen(col)"
              >
                <ng-container *ngIf="col.v2Row.coveredElsewhereUrl; else coveredElsewhereEmpty">
                  <p class="url-topic-ownership-covered-card-lead muted">Similar page on your site</p>
                  <a
                    class="url-topic-ownership-link url-topic-ownership-covered-card-link"
                    [href]="col.v2Row.coveredElsewhereUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ topicOwnershipPagePath(col.v2Row.coveredElsewhereUrl) }}</a
                  >
                  <p class="url-topic-ownership-covered-card-meta muted" *ngIf="col.v2Row.coveredElsewhereLabel">
                    {{ col.v2Row.coveredElsewhereLabel }}
                  </p>
                </ng-container>
                <ng-template #coveredElsewhereEmpty>
                  <p class="muted url-topic-ownership-covered-card-empty">
                    No similar supporting page found elsewhere on your site for this competitor URL.
                  </p>
                </ng-template>
              </div>

              <ul class="url-topic-ownership-col-links" *ngIf="col.supportingRows.length">
                <li *ngFor="let row of getVisibleSupportingRows(col)">
                  <a
                    class="url-topic-ownership-link"
                    [href]="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    [class.url-topic-ownership-link--current]="isCurrentPageSupportingUrl(row.url)"
                    [class.url-topic-ownership-link--covered]="!col.isYourSite && row.similarOnYourSite"
                    [class.url-topic-ownership-link--gap]="!col.isYourSite && !row.similarOnYourSite"
                    [title]="topicOwnershipSupportingLinkTitle(col, row)"
                  >
                    <span class="url-topic-ownership-link-path">{{ topicOwnershipPagePath(row.url) }}</span
                    ><span
                      class="url-topic-ownership-link-traffic"
                      *ngIf="topicOwnershipPageTraffic(row.estimatedOrganicTraffic) as traffic"
                    >
                      ({{ traffic }})</span
                    >
                  </a>
                  <div class="url-topic-ownership-col-row-topics" *ngIf="isDevView() && row.topics?.length">
                    <span
                      class="url-topic-chip"
                      *ngFor="let chip of row.topics"
                      [class.url-topic-chip--main]="chip.role === 'main'"
                      [class.url-topic-chip--supporting]="chip.role === 'supporting'"
                      >{{ chip.topicLabel }}</span
                    >
                  </div>
                </li>
              </ul>

              <button
                type="button"
                class="url-content-topics-expand url-topic-ownership-expand"
                *ngIf="hasMoreSupportingRowsInColumn(col) || isTopicOwnershipPagesExpanded(col.domain)"
                (click)="toggleTopicOwnershipPagesExpanded(col.domain)"
                [attr.aria-expanded]="isTopicOwnershipPagesExpanded(col.domain)"
              >
                {{ supportingRowsExpandLabel(col) }}
              </button>
            </div>
          </div>

          <div class="url-topic-ownership-section-compare-fullrow" *ngIf="getOpenSectionCompareV2Row() as openCompareRow">
            <ng-container
              *ngTemplateOutlet="sectionComparePanel; context: { row: openCompareRow }"
            ></ng-container>
          </div>

          <div class="url-topic-ownership-compare" *ngIf="topicOwnershipCompareOptions().length">
            <label class="url-topic-ownership-compare-label" for="topic-ownership-compare-select">Compare to…</label>
            <select
              id="topic-ownership-compare-select"
              class="url-topic-ownership-compare-select"
              [(ngModel)]="topicOwnershipCompareUrl"
              (ngModelChange)="onTopicOwnershipCompareSelected($event)"
            >
              <option value="">Select competitor page</option>
              <option *ngFor="let opt of topicOwnershipCompareOptions()" [value]="opt.url">{{ opt.label }}</option>
            </select>
          </div>
          </ng-container>
          <app-plan-v2-issue-card-actions
            *ngIf="showIssueCardActions(topicPayload)"
            [payload]="topicPayload"
            [domain]="planV2IssueDomain()"
            (composingChange)="onTopicOwnershipComposingChange($event)"
          />
        </article>
      </div>

      <ng-template #topicCompetitorsCell let-signal="signal">
        <ng-container [ngSwitch]="competitorCoverageStatus">
          <span *ngSwitchCase="'loading'" class="competitor-state muted">Loading…</span>
          <span *ngSwitchCase="'error'" class="competitor-state muted">{{ competitorCoverageMessage || 'Unavailable' }}</span>
          <ng-container *ngSwitchCase="'ready'">
            <div class="competitor-pills" *ngIf="hasCompetitorMatches(signal); else noCompetitorsForTopic">
              <span
                class="competitor-pill-wrap"
                *ngFor="let match of getCompetitorTopicMatches(signal)"
                (mouseenter)="onCompetitorPillHover(match)"
                (mouseleave)="onCompetitorPillLeave()"
              >
                <span class="competitor-pill">{{ formatCompetitorPill(match) }}</span>
                <span
                  class="competitor-popover"
                  *ngIf="isCompetitorTooltipVisible(match)"
                  (mouseenter)="onCompetitorPopoverEnter(match)"
                  (mouseleave)="onCompetitorPopoverLeave()"
                >
                  <ng-container [ngSwitch]="getCompetitorTooltipState(match).status">
                    <span *ngSwitchCase="'loading'">Loading top URLs…</span>
                    <span *ngSwitchCase="'error'">No URL-level preview available.</span>
                    <span *ngSwitchDefault>
                      <span class="competitor-popover-title">{{ match.domain }}</span>
                      <table class="competitor-popover-table">
                        <thead>
                          <tr>
                            <th class="col-url">Page</th>
                            <th class="col-num">Relevance</th>
                            <th class="col-num">Salience</th>
                            <th class="col-num">Traffic/mo</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            *ngFor="let item of getCompetitorTooltipState(match).urls"
                            class="competitor-popover-row"
                          >
                            <td class="col-url">
                              <a
                                href=""
                                class="url"
                                (click)="onCompetitorPopoverUrlClick(match, item.url, $event)"
                                [title]="'Open Quick Compare with ' + item.url"
                              >{{ item.url }}</a>
                            </td>
                            <td class="col-num score">{{ item.score | number:'1.2-2' }}</td>
                            <td class="col-num score">{{ item.salience != null ? (item.salience | number:'1.2-2') : '—' }}</td>
                            <td class="col-num tooltip-url-traffic">{{ item.estimatedOrganicTraffic != null ? (item.estimatedOrganicTraffic | number:'1.0-0') : '—' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </span>
                  </ng-container>
                </span>
              </span>
            </div>
            <ng-template #noCompetitorsForTopic>
              <span class="competitor-state muted">—</span>
            </ng-template>
          </ng-container>
          <span *ngSwitchDefault class="competitor-state muted">—</span>
        </ng-container>
      </ng-template>
    </section>

    <section class="url-focus-row" aria-labelledby="url-freshness-heading" *ngIf="showEmbedFreshnessSection()">
      <h3 id="url-freshness-heading" class="url-focus-row-heading">Freshness</h3>
      <div class="url-focus-row-cards url-focus-row-cards--freshness">
        <article
          class="url-focus-card url-focus-card-freshness"
          *ngIf="buildFreshnessIssuePayload() as freshnessPayload"
          [ngClass]="planV2CardExitClass(freshnessPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Last update</h4>
            <span class="url-focus-diagnosis-chip" [ngClass]="freshnessDiagnosisClass()">{{ freshnessDiagnosisLabel() }}</span>
          </div>
          <dl class="url-focus-dl">
            <div class="url-focus-dl-row">
              <dt>Your page</dt>
              <dd>{{ pageContentLastModifiedLabel() }}</dd>
            </div>
            <div class="url-focus-dl-row" *ngIf="serpWinnersPublishedDateSummary() as serpDates">
              <dt>Top SERP ({{ serpDates.count }} dated)</dt>
              <dd>{{ serpDates.typicalLabel }}</dd>
            </div>
          </dl>
          <p class="url-focus-card-note muted" *ngIf="contentFreshnessVerdict() as verdict">{{ verdict }}</p>
          <app-plan-v2-issue-card-actions
            *ngIf="showIssueCardActions(freshnessPayload)"
            [payload]="freshnessPayload"
            [domain]="planV2IssueDomain()"
          />
        </article>
      </div>
    </section>

    <p class="url-focus-embed-empty muted" *ngIf="embedActionsOnlyEmpty()">
      No actionable findings on these checks — everything looks clear.
    </p>

    <section class="url-focus-row url-focus-row-serp" *ngIf="showEmbedSerpSection()" aria-labelledby="url-serp-heading">
      <div class="url-focus-row-heading-bar">
        <h3 id="url-serp-heading" class="url-focus-row-heading">SERP &amp; INTENT</h3>
        <div class="url-serp-row-toolbar">
          <button
            type="button"
            class="url-serp-expand-btn"
            (click)="toggleSerpSectionExpanded()"
            [attr.aria-expanded]="serpSectionExpanded"
            *ngIf="hasPageSerpSnapshot()"
          >{{ serpSectionExpandLabel() }}</button>
          <div class="url-serp-refresh-actions">
            <button
              type="button"
              class="url-serp-refresh-btn"
              (click)="refreshUrlSerpForDomain()"
              [disabled]="urlSerpRefreshRunning"
            >{{ urlSerpRefreshRunning ? 'Refreshing…' : 'Refresh SERP' }}</button>
            <label class="url-serp-force-label" title="Bypass SERP and keyword-difficulty caches; re-fetch from API.">
              <input type="checkbox" [(ngModel)]="urlSerpForceRefresh" [disabled]="urlSerpRefreshRunning" />
              Force API
            </label>
          </div>
        </div>
      </div>
      <p class="url-focus-row-meta muted" *ngIf="hasPageSerpSnapshot()">
        {{ pageSerpTargetQueryLabel() }} · {{ pageSerpFetchedLabel() }} · {{ pageSerpQuerySourceLabel() }}
      </p>
      <p class="muted" *ngIf="!hasPageSerpSnapshot()">{{ pageSerpPendingReason() }}</p>

      <div
        class="url-serp-inline-progress"
        *ngIf="urlSerpRefreshRunning && urlSerpRefreshProgress"
        role="status"
        aria-live="polite"
      >
        <div class="url-serp-inline-progress-bar">
          <div class="url-serp-inline-progress-fill" [style.width.%]="urlSerpRefreshProgress.percent"></div>
        </div>
        <p class="url-serp-inline-progress-line">{{ urlSerpRefreshProgress.statusLine }}</p>
        <p class="url-serp-inline-progress-eta muted" *ngIf="urlSerpRefreshProgress.etaLabel">{{ urlSerpRefreshProgress.etaLabel }}</p>
      </div>
      <p class="url-focus-card-note muted url-serp-scope-hint" *ngIf="isDevView()">
        Refresh updates <strong>this URL’s target query only</strong>. Use Inventory → Refresh URL SERP for the full domain.
      </p>

      <div class="url-serp-cards-row">
        <ng-container *ngIf="showEmbedSerpCompetitionCard() && serpCompetitionInsight() as competition">
        <article
          class="url-focus-card url-serp-subcard url-serp-subcard-competition"
          *ngIf="buildSerpCompetitionIssuePayload() as serpCompPayload"
          [ngClass]="planV2CardExitClass(serpCompPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Who’s beating you?</h4>
            <span class="url-focus-diagnosis-chip" [ngClass]="serpCompetitionChipClass()">{{
              serpCompetitionChipLabel()
            }}</span>
          </div>
          <dl class="url-focus-dl url-serp-competition-summary">
            <div class="url-focus-dl-row">
              <dt>Your rank</dt>
              <dd>{{ serpCompetitionYourRankLabel() }}</dd>
            </div>
            <div class="url-focus-dl-row" *ngIf="markedCompetitorDomains.length">
              <dt>Rival ahead (top 5)</dt>
              <dd>{{ serpCompetitionMarkedAboveLabel() }}</dd>
            </div>
          </dl>
          <p class="url-serp-competition-hint muted" *ngIf="!markedCompetitorDomains.length">
            Mark competitors in settings to track marked rivals on this SERP.
          </p>

          <ng-container *ngIf="competition.markedAboveYou">
            <ul class="url-serp-competition-rivals">
              <li *ngFor="let rival of competition.markedCompetitorsAbove" class="url-serp-competition-rival">
                <div class="url-serp-competition-rival-head">
                  <span class="url-serp-competition-pos">#{{ rival.position }}</span>
                  <span class="url-serp-competition-domain">{{ rival.domain }}</span>
                </div>
                <p class="url-serp-competition-title">{{ rival.title }}</p>
                <button type="button" class="url-serp-competition-compare-btn" (click)="openQuickCompareWithUrl(rival.url)">
                  Quick Compare
                </button>
              </li>
            </ul>
            <p class="url-serp-competition-note muted">Uses workspace crawl when available.</p>
          </ng-container>

          <ng-container *ngIf="!competition.markedAboveYou">
            <p class="url-serp-competition-leaders-intro muted" *ngIf="competition.topLeaders.length">
              Top 3 on this SERP (excluding your URL):
            </p>
            <div class="url-serp-competition-leaders" *ngIf="competition.topLeaders.length">
              <div *ngFor="let row of competition.topLeaders" class="url-serp-competition-leader">
                <div class="url-serp-competition-leader-head">
                  <span class="url-serp-competition-pos">#{{ row.position }}</span>
                  <a
                    class="url-serp-competition-domain"
                    [href]="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ row.domain }}</a>
                  <span class="url-serp-competition-date muted" *ngIf="row.publishedLabel">{{ row.publishedLabel }}</span>
                </div>
                <p class="url-serp-competition-title">{{ row.title }}</p>
                <p class="url-serp-competition-meta muted" *ngIf="row.descriptionPreview">{{ row.descriptionPreview }}</p>
              </div>
            </div>
            <p class="url-serp-competition-note muted" *ngIf="!competition.topLeaders.length">
              No other domains in the top 3 slots for this query.
            </p>
          </ng-container>
        </article>
        </ng-container>

        <article
          class="url-focus-card url-serp-subcard url-serp-subcard-phrase"
          *ngIf="showEmbedSerpPhraseCard() && buildSerpPhraseIssuePayload() as serpPhrasePayload"
          [ngClass]="planV2CardExitClass(serpPhrasePayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">SERP phrase match</h4>
            <span
              class="url-focus-diagnosis-chip"
              *ngIf="showSerpPhraseMatchActionChip()"
              [ngClass]="serpPhraseMatchActionChipClass()"
            >{{ serpPhraseMatchActionChipLabel() }}</span>
          </div>
          <p class="url-serp-compare-legend muted" *ngIf="pageIntentDiagnosis?.serpVocabularyShared">
            <span class="url-serp-legend-green">Green</span> aligned · <span class="url-serp-legend-amber">amber</span> wrong context · <span class="url-serp-legend-red">red</span> missing · hover chip → winners
          </p>
          <p class="url-serp-vocab-fragmented muted" *ngIf="showFragmentedSerpVocabularyNote()">
            No shared vocabulary across top snippets — compare intent and table highlights.
          </p>
          <p class="url-serp-vocab-chips" *ngIf="hasLexicalVocabChips()">
            <span
              class="url-serp-present-chip url-serp-vocab-chip-interactive"
              *ngFor="let term of pageIntentDiagnosis?.presentLexicalTerms"
              [class.url-serp-vocab-chip-active]="isVocabChipHovered(term)"
              [title]="pageIntentVocabChipTitle(term)"
              (mouseenter)="onVocabChipHover(term)"
              (mouseleave)="onVocabChipHover(null)"
              (focus)="onVocabChipHover(term)"
              (blur)="onVocabChipHover(null)"
              tabindex="0"
            >{{ term }}</span>
            <span
              class="url-serp-misaligned-chip url-serp-vocab-chip-interactive"
              *ngFor="let term of pageIntentDiagnosis?.misalignedLexicalTerms"
              [class.url-serp-vocab-chip-active]="isVocabChipHovered(term)"
              [title]="pageIntentMisalignedChipTitle(term)"
              (mouseenter)="onVocabChipHover(term)"
              (mouseleave)="onVocabChipHover(null)"
              (focus)="onVocabChipHover(term)"
              (blur)="onVocabChipHover(null)"
              tabindex="0"
            >{{ term }}</span>
            <span
              class="url-serp-missing-chip url-serp-vocab-chip-interactive"
              *ngFor="let term of pageIntentDiagnosis?.missingTerms"
              [class.url-serp-vocab-chip-active]="isVocabChipHovered(term)"
              [title]="pageIntentVocabChipTitle(term)"
              (mouseenter)="onVocabChipHover(term)"
              (mouseleave)="onVocabChipHover(null)"
              (focus)="onVocabChipHover(term)"
              (blur)="onVocabChipHover(null)"
              tabindex="0"
            >{{ term }}</span>
          </p>

          <div class="url-serp-compare" *ngIf="hasPageIntentCompare()">
            <div class="url-serp-compare-col url-serp-compare-yours">
              <h6 class="url-serp-compare-heading url-serp-compare-heading-plain">our page</h6>

              <section
                class="url-serp-page-section"
                *ngIf="pageIntentDiagnosis?.yourBestSection as best"
              >
                <h6 class="url-serp-page-section-label">{{ best.label || 'Best section' }}</h6>
                <p class="url-serp-compare-text">
                  <ng-container *ngFor="let seg of pageIntentYourSectionSegments(best.text)">
                    <mark class="url-serp-contract-term" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                    <mark
                      *ngIf="seg.misaligned"
                      class="url-serp-misaligned-term"
                      tabindex="0"
                      [attr.aria-label]="pageIntentMisalignedAriaLabel(seg)"
                    >
                      {{ seg.text }}
                      <span class="url-serp-misaligned-tip" *ngIf="seg.misalignedLink" role="tooltip">
                        <span class="url-serp-misaligned-tip-row">
                          <span class="url-serp-misaligned-tip-label">Your page</span>
                          <span class="url-serp-misaligned-tip-phrase">{{ seg.misalignedLink.head }} → {{ seg.misalignedLink.actual }}</span>
                        </span>
                        <span class="url-serp-misaligned-tip-row">
                          <span class="url-serp-misaligned-tip-label">Winners</span>
                          <span class="url-serp-misaligned-tip-phrase">{{ seg.misalignedLink.head }} → {{ seg.misalignedLink.expected }}</span>
                        </span>
                      </span>
                    </mark>
                    <ng-container *ngIf="!seg.emphasized && !seg.misaligned">{{ seg.text }}</ng-container>
                  </ng-container>
                </p>
              </section>
              <p class="muted url-serp-compare-empty" *ngIf="!pageIntentDiagnosis?.yourBestSection?.text">
                No crawled section text available for this URL.
              </p>

              <section
                class="url-serp-page-section url-serp-page-section-secondary"
                *ngIf="pageIntentDiagnosis?.yourIntroSection as intro"
              >
                <h6 class="url-serp-page-section-label">{{ intro.label }}</h6>
                <p class="url-serp-compare-text">
                  <ng-container *ngFor="let seg of pageIntentYourSectionSegments(intro.text)">
                    <mark class="url-serp-contract-term" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                    <mark
                      *ngIf="seg.misaligned"
                      class="url-serp-misaligned-term"
                      tabindex="0"
                      [attr.aria-label]="pageIntentMisalignedAriaLabel(seg)"
                    >
                      {{ seg.text }}
                      <span class="url-serp-misaligned-tip" *ngIf="seg.misalignedLink" role="tooltip">
                        <span class="url-serp-misaligned-tip-row">
                          <span class="url-serp-misaligned-tip-label">Your page</span>
                          <span class="url-serp-misaligned-tip-phrase">{{ seg.misalignedLink.head }} → {{ seg.misalignedLink.actual }}</span>
                        </span>
                        <span class="url-serp-misaligned-tip-row">
                          <span class="url-serp-misaligned-tip-label">Winners</span>
                          <span class="url-serp-misaligned-tip-phrase">{{ seg.misalignedLink.head }} → {{ seg.misalignedLink.expected }}</span>
                        </span>
                      </span>
                    </mark>
                    <ng-container *ngIf="!seg.emphasized && !seg.misaligned">{{ seg.text }}</ng-container>
                  </ng-container>
                </p>
              </section>
            </div>

            <div class="url-serp-compare-col url-serp-compare-winners">
              <h6 class="url-serp-compare-heading">What top results say</h6>
              <p class="url-serp-compare-idle muted" *ngIf="!hoveredVocabChip">
                Hover a vocabulary chip to see matching snippets from the top 10.
              </p>
              <ng-container *ngIf="hoveredVocabChip as chip">
                <p class="url-serp-compare-chip-active muted">“{{ chip }}” in winners</p>
                <ng-container *ngIf="pageSerpCompareWinnerRows().length; else noWinnerChipMatches">
                  <div class="url-serp-compare-excerpt" *ngFor="let row of pageSerpCompareWinnerRows(); trackBy: trackPageSerpRow">
                    <span class="url-serp-compare-meta">#{{ row.position }} · {{ row.domain }}</span>
                    <p class="url-serp-compare-text url-serp-compare-text-chip-hover">
                      <ng-container *ngFor="let seg of pageSerpCompareWinnerRowSegments(row)">
                        <mark class="url-serp-contract-term url-serp-chip-pick" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                        <ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container>
                      </ng-container>
                    </p>
                  </div>
                </ng-container>
                <ng-template #noWinnerChipMatches>
                  <p class="url-serp-compare-empty muted">No top-10 snippets contain “{{ chip }}”.</p>
                </ng-template>
              </ng-container>
            </div>
          </div>
          <app-plan-v2-issue-card-actions
            *ngIf="showIssueCardActions(serpPhrasePayload)"
            [payload]="serpPhrasePayload"
            [domain]="planV2IssueDomain()"
          />
        </article>

        <article
          class="url-focus-card url-serp-subcard url-serp-subcard-kd"
          *ngIf="showEmbedSerpDifficultyCard() && buildSerpDifficultyIssuePayload() as serpKdPayload"
          [ngClass]="planV2CardExitClass(serpKdPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">SERP difficulty</h4>
            <span class="url-focus-diagnosis-chip" [ngClass]="serpDifficultyActionChipClass()">{{
              serpDifficultyActionChipLabel()
            }}</span>
          </div>
          <p class="url-serp-kd-value" [attr.title]="pageSerpKeywordDifficultyTitle()">{{ serpKeywordDifficultyShort() }}</p>
          <p class="url-serp-kd-sub muted" *ngIf="serpKeywordDifficultyHint() as hint">{{ hint }}</p>
          <p class="url-serp-kd-query muted">{{ pageSerpTargetQueryLabel() }}</p>
        </article>

        <article
          class="url-focus-card url-serp-subcard url-serp-subcard-intent"
          *ngIf="showEmbedSerpIntentCard() && buildSerpIntentIssuePayload() as serpIntentPayload"
          [ngClass]="planV2CardExitClass(serpIntentPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Intent vs. your page</h4>
            <span class="url-focus-diagnosis-chip" [ngClass]="pageIntentMatchActionChipClass()">{{
              pageIntentMatchActionChipLabel()
            }}</span>
          </div>
          <div class="url-serp-intent-head" *ngIf="showPageIntentAlignmentRow()">
            <span class="url-serp-intent-pair">
              <span class="url-serp-intent-pair-label muted">SERP</span>
              <span [ngClass]="pageIntentSerpFamilyChipClass()">{{ pageIntentFamilyLabel(pageIntentDiagnosis?.serpFamily) }}</span>
            </span>
            <span class="url-serp-intent-pair">
              <span class="url-serp-intent-pair-label muted">Your page</span>
              <span [ngClass]="pageIntentYourFamilyChipClass()">{{ pageIntentYourPageFamilyLabel() }}</span>
            </span>
          </div>
          <table
            class="url-serp-mixed-table"
            *ngIf="pageIntentDiagnosis?.serpFamily === 'mixed' && pageIntentDiagnosis?.serpMixedIntentSamples?.length"
          >
            <thead>
              <tr>
                <th class="url-serp-mixed-col-pos">#</th>
                <th class="url-serp-mixed-col-domain">Domain</th>
                <th class="url-serp-mixed-col-intent">Intent</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let sample of pageIntentDiagnosis?.serpMixedIntentSamples">
                <td class="url-serp-mixed-col-pos">{{ sample.position }}</td>
                <td class="url-serp-mixed-col-domain" [title]="sample.domain">{{ sample.domain }}</td>
                <td class="url-serp-mixed-col-intent">
                  <span [ngClass]="pageIntentFamilyChipClassFor(sample.family)">{{ pageIntentFamilyLabel(sample.family) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="url-serp-intent-brief muted" *ngFor="let line of pageIntentBriefLines()">{{ line }}</p>
          <app-plan-v2-issue-card-actions
            *ngIf="showIssueCardActions(serpIntentPayload)"
            [payload]="serpIntentPayload"
            [domain]="planV2IssueDomain()"
          />
        </article>
      </div>

      <div class="url-serp-expanded" *ngIf="showEmbedSerpResultsTable()">
        <div class="page-detail-table-wrap url-serp-table-wrap">
          <table class="page-detail-table url-serp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Domain</th>
                <th>Title</th>
                <th>Intent</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let row of pageSerp?.results; trackBy: trackPageSerpRow"
                [class.url-serp-row-you]="pageSerpResultCompetitorKind(row) === 'you'"
                [class.url-serp-row-marked]="pageSerpResultCompetitorKind(row) === 'marked'"
              >
                <td>{{ row.position }}</td>
                <td>
                  <a [href]="'https://' + row.domain" target="_blank" rel="noopener noreferrer">{{ row.domain }}</a>
                </td>
                <td class="url-serp-title-cell">
                  <a [href]="row.url" target="_blank" rel="noopener noreferrer">{{ row.title }}</a>
                  <p
                    class="url-serp-published"
                    *ngIf="pageSerpPublishedDate(row) as pubDate"
                    [ngClass]="pageSerpPublishedAgeClass(row)"
                  >{{ pubDate | date:'MMM d, yyyy' }}</p>
                  <p class="url-serp-snippet" *ngIf="row.description">
                    <ng-container *ngFor="let seg of pageSerpSnippetSegments(row)">
                      <strong *ngIf="seg.emphasized; else serpSnippetPlain">{{ seg.text }}</strong>
                      <ng-template #serpSnippetPlain>{{ seg.text }}</ng-template>
                    </ng-container>
                  </p>
                </td>
                <td class="url-serp-intent-col">
                  <span [ngClass]="pageIntentFamilyChipClassFor(pageSerpRowIntentFamily(row))">{{
                    pageSerpRowIntentLabel(row)
                  }}</span>
                </td>
                <td>
                  <span
                    class="url-serp-badge"
                    [class.url-serp-badge-you]="pageSerpResultCompetitorKind(row) === 'you'"
                    [class.url-serp-badge-marked]="pageSerpResultCompetitorKind(row) === 'marked'"
                    [class.url-serp-badge-unknown]="pageSerpResultCompetitorKind(row) === 'unknown'"
                  >{{ pageSerpResultCompetitorLabel(row) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="url-focus-row" *ngIf="showEmbedTechnicalSection()" aria-labelledby="url-technical-heading">
      <h3 id="url-technical-heading" class="url-focus-row-heading">Technical</h3>
      <div class="url-focus-row-cards url-focus-row-cards--technical">
        <article
          class="url-focus-card url-focus-card-technical-critical"
          *ngIf="showEmbedTechnicalIssuesCard() && buildTechnicalCriticalIssuePayload() as techCritPayload"
          [class.url-focus-card--composing]="technicalCriticalComposing"
          [ngClass]="planV2CardExitClass(techCritPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Issues</h4>
            <span class="url-focus-diagnosis-chip" [ngClass]="technicalIssuesActionChipClass()">{{
              technicalIssuesActionChipLabel()
            }}</span>
          </div>
          <ng-container *ngIf="!technicalCriticalComposing">
            <p class="url-technical-none muted" *ngIf="!hasTechnicalCriticalIssues()">None</p>
            <ul *ngIf="hasTechnicalCriticalIssues()" class="url-focus-list url-technical-issue-list">
              <ng-container
                *ngFor="let issue of technicalCriticalIssueViews(); let i = index"
                [ngTemplateOutlet]="onpageIssueItem"
                [ngTemplateOutletContext]="{ issue: issue, kind: 'critical', index: i }"
              ></ng-container>
            </ul>
          </ng-container>
          <app-plan-v2-issue-card-actions
            *ngIf="showIssueCardActions(techCritPayload)"
            [payload]="techCritPayload"
            [domain]="planV2IssueDomain()"
            (composingChange)="technicalCriticalComposing = $event"
          />
        </article>

        <article
          class="url-focus-card url-focus-card-technical-warnings"
          *ngIf="showEmbedTechnicalWarningsCard() && buildTechnicalWarningsIssuePayload() as techWarnPayload"
          [class.url-focus-card--composing]="technicalWarningsComposing"
          [ngClass]="planV2CardExitClass(techWarnPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Warnings</h4>
            <span class="url-focus-diagnosis-chip" [ngClass]="technicalWarningsActionChipClass()">{{
              technicalWarningsActionChipLabel()
            }}</span>
          </div>
          <ng-container *ngIf="!technicalWarningsComposing">
            <p class="url-technical-none muted" *ngIf="!hasTechnicalWarnings()">None</p>
            <ul *ngIf="hasTechnicalWarnings()" class="url-focus-list url-technical-issue-list">
              <ng-container
                *ngFor="let issue of technicalWarningIssueViews(); let i = index"
                [ngTemplateOutlet]="onpageIssueItem"
                [ngTemplateOutletContext]="{ issue: issue, kind: 'warning', index: i }"
              ></ng-container>
            </ul>
          </ng-container>
          <app-plan-v2-issue-card-actions
            *ngIf="showIssueCardActions(techWarnPayload)"
            [payload]="techWarnPayload"
            [domain]="planV2IssueDomain()"
            (composingChange)="technicalWarningsComposing = $event"
          />
        </article>

        <ng-template #onpageIssueItem let-issue="issue" let-kind="kind" let-index="index">
          <li class="url-technical-issue-item">
            <button
              *ngIf="issue.details.length"
              type="button"
              class="url-technical-issue-title url-technical-issue-title--toggle"
              [attr.aria-expanded]="isTechnicalIssueDetailsExpanded(kind, index)"
              (click)="toggleTechnicalIssueDetails(kind, index)"
            >
              <span class="url-technical-issue-chevron" [class.rotated]="isTechnicalIssueDetailsExpanded(kind, index)"
                >▼</span
              >
              <span class="url-technical-issue-title-text">{{ issue.title }}</span>
            </button>
            <span *ngIf="!issue.details.length" class="url-technical-issue-title">{{ issue.title }}</span>
            <ul
              *ngIf="issue.details.length && isTechnicalIssueDetailsExpanded(kind, index)"
              class="url-technical-issue-details"
            >
              <li *ngFor="let detail of issue.details">{{ detail }}</li>
            </ul>
          </li>
        </ng-template>

        <article class="url-focus-card url-focus-card-technical-perf" *ngIf="!isPlanV2Embed()">
          <h4 class="url-focus-card-title">Technical performance</h4>
          <dl class="url-focus-dl url-technical-perf-dl">
            <div class="url-focus-dl-row">
              <dt>TTI</dt>
              <dd>{{ formatTechnicalPerfMs(analysisData.timeToInteractive) }}</dd>
            </div>
            <div class="url-focus-dl-row">
              <dt>LCP</dt>
              <dd>{{ formatTechnicalPerfMs(analysisData.largestContentfulPaint) }}</dd>
            </div>
            <div class="url-focus-dl-row">
              <dt>FID</dt>
              <dd>{{ formatTechnicalPerfMs(analysisData.firstInputDelay) }}</dd>
            </div>
          </dl>
        </article>

        <article
          *ngIf="!isPlanV2Embed()"
          class="url-focus-card url-focus-card-technical-links"
          [class.url-focus-card-technical-links--dev]="isDevView()"
        >
          <h4 class="url-focus-card-title">Internal links</h4>
          <p class="url-technical-none muted" *ngIf="!hasLinks()">None</p>
          <ng-container *ngIf="hasLinks()">
            <ng-container *ngIf="isDevView(); else technicalLinksUser">
              <div class="url-technical-link-stats">
                <div class="url-technical-link-stat">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().structural }}</span>
                  <span class="url-technical-link-stat-label">Structural</span>
                </div>
                <div class="url-technical-link-stat">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().contextual }}</span>
                  <span class="url-technical-link-stat-label">Contextual</span>
                </div>
                <div class="url-technical-link-stat">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().samePage }}</span>
                  <span class="url-technical-link-stat-label">Same page</span>
                </div>
                <div class="url-technical-link-stat" *ngIf="getLinkStats().unknown > 0">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().unknown }}</span>
                  <span class="url-technical-link-stat-label">Unknown</span>
                </div>
              </div>
              <div class="links-controls url-technical-links-controls">
                <div class="filter-group">
                  <label>Filter:</label>
                  <select [(ngModel)]="linkFilter" class="filter-select">
                    <option value="all">All Links</option>
                    <option value="structural">Structural</option>
                    <option value="contextual">Contextual</option>
                    <option value="same_page">Same Page</option>
                  </select>
                </div>
                <div class="filter-group">
                  <label>Sort:</label>
                  <select [(ngModel)]="linkSort" class="filter-select">
                    <option value="type">By Type</option>
                    <option value="confidence">By Confidence</option>
                    <option value="anchor">By Anchor Text</option>
                    <option value="url">By URL</option>
                  </select>
                </div>
              </div>
              <div class="links-list url-technical-links-list">
                <div
                  *ngFor="let link of getFilteredLinks(); trackBy: trackByLink"
                  class="link-item"
                  [class.expanded]="link._expanded"
                >
                  <div class="link-header" (click)="toggleLinkDetails(link)">
                    <div class="link-main">
                      <span class="link-badge" [class]="getLinkTypeClass(link.linkType)">
                        {{ getLinkTypeLabel(link.linkType) }}
                      </span>
                      <span class="link-anchor-text">{{ link.anchorText || '(no anchor text)' }}</span>
                    </div>
                    <div class="link-meta">
                      <span class="link-confidence" [class]="getConfidenceClass(link.confidence)">
                        {{ (link.confidence * 100).toFixed(0) }}%
                      </span>
                      <span class="link-chevron" [class.rotated]="link._expanded">▼</span>
                    </div>
                  </div>
                  <div class="link-url">
                    <a [href]="link.targetUrl" target="_blank" rel="noopener noreferrer" (click)="$event.stopPropagation()">
                      {{ link.targetRelativeUrl }}
                    </a>
                    <span class="link-flags">
                      <span *ngIf="!link.dofollow" class="flag flag-nofollow">nofollow</span>
                      <span *ngIf="link.isBroken" class="flag flag-broken">broken</span>
                    </span>
                  </div>
                  <div class="link-details" *ngIf="link._expanded">
                    <div class="detail-grid">
                      <div class="detail-item" *ngIf="link.classificationSignals?.linkFrequency !== undefined">
                        <span class="detail-label">Frequency:</span>
                        <span class="detail-value">
                          {{ link.classificationSignals.linkFrequency }} pages
                          <span *ngIf="link.classificationSignals.linkFrequencyRatio !== undefined">
                            ({{ (link.classificationSignals.linkFrequencyRatio * 100).toFixed(1) }}%)
                          </span>
                        </span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.anchorTextLength !== undefined">
                        <span class="detail-label">Anchor Length:</span>
                        <span class="detail-value">{{ link.classificationSignals.anchorTextLength }} chars</span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.urlPattern">
                        <span class="detail-label">URL Pattern:</span>
                        <span class="detail-value">{{ link.classificationSignals.urlPattern }}</span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.isGenericAnchor !== undefined">
                        <span class="detail-label">Generic Anchor:</span>
                        <span class="detail-value">{{ link.classificationSignals.isGenericAnchor ? 'Yes' : 'No' }}</span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.isSamePage !== undefined">
                        <span class="detail-label">Same Page:</span>
                        <span class="detail-value">{{ link.classificationSignals.isSamePage ? 'Yes' : 'No' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p class="url-technical-none muted" *ngIf="getFilteredLinks().length === 0">No links match the current filter.</p>
            </ng-container>
            <ng-template #technicalLinksUser>
              <dl class="url-technical-link-counts">
                <div class="url-focus-dl-row">
                  <dt>Structural</dt>
                  <dd>{{ getLinkStats().structural }}</dd>
                </div>
                <div class="url-focus-dl-row">
                  <dt>Contextual</dt>
                  <dd>{{ getLinkStats().contextual }}</dd>
                </div>
                <div class="url-focus-dl-row">
                  <dt>Same page</dt>
                  <dd>{{ getLinkStats().samePage }}</dd>
                </div>
              </dl>
            </ng-template>
          </ng-container>
        </article>
      </div>
    </section>

    <ng-template #internalLinkSuggestionRows>
      <ul class="url-signals-detail-list url-signals-detail-list--links">
        <li *ngFor="let row of pageSignalInternalLinkDetailsForDisplay()" class="url-signals-link-row">
          <div class="url-signals-link-row-main" *ngIf="internalLinkRowDisplay(row) as display">
            <div class="internal-link-row-chips">
              <span class="internal-link-direction-chip" [ngClass]="internalLinkDirectionChipClasses(row)">{{
                internalLinkDirectionChip(row)
              }}</span>
              <span class="internal-link-relevance-chip"
                >Relevance {{ formatInternalLinkScore(row.score) }}</span
              >
            </div>
            <a
              class="url-signals-detail-link url-signals-detail-link--path"
              [routerLink]="inventoryDetailLink(display.pageUrl)"
              [title]="display.pageUrl"
              >{{ display.pagePath }}</a
            >
            <span class="url-signals-detail-meta muted" *ngIf="row.matchedTopic">
              topic {{ row.matchedTopic }}</span
            >
            <p class="url-signals-placement muted" *ngIf="row.where?.sentence as sentence">
              <ng-container *ngIf="internalLinkPlacementParts(row) as parts; else signalPlacementPlain">
                “{{ parts.before }}<a
                  class="url-signals-placement-link"
                  [href]="display.linkHref"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ parts.anchor }}</a
                >{{ parts.after }}”
              </ng-container>
              <ng-template #signalPlacementPlain>
                “{{ sentence }}”
              </ng-template>
            </p>
          </div>
        </li>
      </ul>
    </ng-template>

    <section
      class="url-focus-row"
      *ngIf="showEmbedInternalLinksSection()"
      aria-labelledby="url-internal-links-row-heading"
    >
      <h3 id="url-internal-links-row-heading" class="url-focus-row-heading">Internal links</h3>
      <article
        #internalLinksSection
        class="url-focus-card url-focus-card--internal-links"
        *ngIf="internalLinksIssuesPayload() as linksPayload"
        [class.url-focus-card--composing]="internalLinksComposing"
        [ngClass]="planV2CardExitClass(linksPayload.issueKey)"
      >
        <div class="url-focus-card-top">
          <h4 class="url-focus-card-title" id="url-internal-links-heading">Suggestions</h4>
        </div>
        <ng-container *ngIf="!internalLinksComposing">
          <ng-container *ngTemplateOutlet="internalLinkSuggestionRows"></ng-container>
        </ng-container>
        <app-plan-v2-issue-card-actions
          *ngIf="showIssueCardActions(linksPayload)"
          [payload]="linksPayload"
          [domain]="planV2IssueDomain()"
          (composingChange)="onInternalLinksComposingChange($event)"
        />
      </article>
    </section>

    <section class="url-focus-row" *ngIf="showEmbedSignalsSection()" aria-labelledby="url-signals-heading">
      <h3 id="url-signals-heading" class="url-focus-row-heading">Signals</h3>
      <article
        class="url-focus-card url-signals-card"
        [ngClass]="isPlanV2Embed() ? planV2CardExitClass(planV2IssueKey('signals')) : {}"
      >
        <p class="url-signals-loading muted" *ngIf="pageSignalsLoading">Loading workspace signals…</p>

        <ng-container *ngIf="!pageSignalsLoading">
          <div class="page-signals" *ngIf="pageSignals.length > 0">
            <span
              class="page-signal-chip"
              *ngFor="let signal of pageSignals"
              [ngClass]="pageSignalChipClass(signal.kind)"
              [title]="signal.title"
            >{{ signal.label }}</span>
          </div>
          <p class="url-signals-empty muted" *ngIf="pageSignals.length === 0">
            No open action-plan or cluster signals for this URL (same rules as Inventory → Pages → Signals).
          </p>

          <div class="url-signals-plan-clusters" *ngIf="planClusters.length > 0">
            <h4 class="url-signals-detail-title">Plan clusters</h4>
            <p class="url-signals-cluster-tags">
              <span class="cluster-tag" *ngFor="let label of planClusters">{{ label }}</span>
            </p>
          </div>

          <div class="url-signals-detail" *ngIf="pageSignalCannibalDetails().length > 0">
            <h4 class="url-signals-detail-title">Cannibalization</h4>
            <ul class="url-signals-detail-list">
              <li *ngFor="let row of pageSignalCannibalDetails()">
                <span class="url-signals-detail-main">Open pair with</span>
                <a
                  class="url-signals-detail-link"
                  [routerLink]="inventoryDetailLink(pageSignalCannibalCounterpart(row))"
                  [title]="pageSignalCannibalCounterpart(row)"
                >{{ pageSignalCannibalCounterpart(row) }}</a>
              </li>
            </ul>
          </div>

          <div
            class="url-signals-detail url-signals-detail--internal-links"
            *ngIf="!isPlanV2Embed() && pageSignalInternalLinkDetails().length > 0"
          >
            <h4 class="url-signals-detail-title">Internal link suggestions</h4>
            <ng-container *ngTemplateOutlet="internalLinkSuggestionRows"></ng-container>
          </div>

          <div class="url-signals-detail" *ngIf="pageSignalUnclusteredDetail() as unclustered">
            <h4 class="url-signals-detail-title">Unclustered page</h4>
            <p class="url-signals-detail-line muted">
              Recommendation: <strong>{{ unclusteredRecommendationLabel(unclustered.recommendation) }}</strong>
            </p>
            <p class="url-signals-detail-line muted" *ngIf="unclustered.topTopics?.length">
              Top topics: {{ unclustered.topTopics.join(', ') }}
            </p>
          </div>

          <div class="url-signals-detail" *ngIf="hasPageSignalKind('no_cluster')">
            <h4 class="url-signals-detail-title">No plan cluster</h4>
            <p class="url-signals-detail-line muted">
              This URL is not in any workspace plan cluster for this domain. Assign clusters in Content Inventory → Plan.
            </p>
          </div>

          <p class="url-signals-footer" *ngIf="!isPlanV2Embed()">
            <a class="url-signals-inventory-link" routerLink="/inventory" (click)="openInventoryForSignals()">
              Open Content Inventory
            </a>
            <span class="muted"> to resolve signals on the Plan or Cannibalization tabs.</span>
          </p>
          <ng-container *ngIf="isPlanV2Embed() && buildSignalsIssuePayload() as signalsPayload">
            <app-plan-v2-issue-card-actions
              *ngIf="showIssueCardActions(signalsPayload)"
              [payload]="signalsPayload"
              [domain]="planV2IssueDomain()"
            />
          </ng-container>
        </ng-container>
      </article>
    </section>

    <section
      class="url-focus-row url-focus-row--dev-content"
      aria-labelledby="url-dev-content-heading"
      *ngIf="!isPlanV2Embed() && isDevView() && analysisData && hasContent()"
    >
      <h3 id="url-dev-content-heading" class="url-focus-row-heading">Page Content</h3>
      <div class="content-section url-focus-dev-page-content">
      <div class="content-header" (click)="toggleContentSection()">
        <h4 class="url-focus-card-title">{{ contentElements.length }} elements</h4>
        <span class="chevron" [class.collapsed]="contentSectionCollapsed">▼</span>
      </div>
      
      <div class="content-body" [class.collapsed]="contentSectionCollapsed">
        <div class="content-info">
          <span>Key content elements extracted from the page</span>
        </div>
        
        <div class="content-elements">
          <div 
            *ngFor="let element of contentElements; trackBy: trackByElement" 
            class="content-element"
            [class]="element.type">
            
            <!-- Headings -->
            <div *ngIf="element.type === 'heading'" class="heading-element">
              <span class="heading-tag">H{{ element.level }}</span>
              <span class="heading-text" [class]="getHeadingClass(element.level!)">
                {{ element.content }}
              </span>
            </div>
            
            <!-- Text Content -->
            <div *ngIf="element.type === 'text'" class="text-element">
              <span class="text-content">{{ element.content }}</span>
            </div>
            
            <!-- Links -->
            <div *ngIf="element.type === 'link'" class="link-element">
              <span class="link-text">{{ element.content }}</span>
              <span class="link-url">{{ element.href }}</span>
            </div>
            
            <!-- Tables -->
            <div *ngIf="element.type === 'table'" class="table-element">
              <div class="table-header">{{ element.content }}</div>
              <div class="table-content">
                <div class="table-row">
                  <div 
                    *ngFor="let cell of element.tableData; trackBy: trackByElement" 
                    class="table-cell"
                    [class.header-cell]="cell.isHeader">
                    <span class="cell-text">{{ cell.text }}</span>
                    <span *ngIf="cell.urls && cell.urls.length > 0" class="cell-links">
                      <a *ngFor="let url of cell.urls" [href]="url.url" target="_blank" class="cell-link">
                        {{ url.anchor_text || url.url }}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </section>

  </ng-container>
</section>

<app-inventory-quick-compare-drawer
  [open]="quickCompareOpen"
  [primaryUrl]="quickComparePrimaryUrl"
  [compareUrl]="quickCompareCompareUrl"
  [primaryDomainLabel]="extractDomainFromUrl(url || '')"
  [compareDomainLabel]="extractDomainFromUrl(quickCompareCompareUrl || '')"
  (closed)="onQuickCompareDrawerClose()"
  (openFullCompare)="onOpenFullCompareFromDrawer()"
/> 